// ╔════════════════════════════════════════════════════════════════════════════╗
// ║  SETTINGSTAB.JSX - VERSION: 2025.12.29.0215                                ║
// ╚════════════════════════════════════════════════════════════════════════════╝
// ============================================================================
// 📦 VERSION: 2025.12.29.0215
// ⚙️ Settings Tab - Profile & Preferences
// ============================================================================
// CHANGELOG:
// 2025.12.29.0215 - Added Edit Profile section (change name, password reset)
// 2025.12.13.1430 - Added proper links to Help, Terms, Privacy, GDPR pages
// 2025.12.03.0001 - Fixed Picture Grid toggle (was using wrong localStorage key), default to enabled
// 2025.12.02.1820 - Initial version
// ============================================================================
import React, { useState, useEffect } from 'react';

const API_URL = 'https://api.traktag.com/api';

const SettingsTab = ({ 
  playerData, 
  onUpdateProfile,
  onUpdateHandedness,
  notifications = [],
  onMarkNotificationRead,
  pictureGridSettings,
  onUpdatePictureGridSettings
}) => {
  const [showNotifications, setShowNotifications] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  
  // Edit profile states
  const [editName, setEditName] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [editSuccess, setEditSuccess] = useState('');
  const [editError, setEditError] = useState('');
  
  // Password reset states
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');
  
  const unreadCount = notifications.filter(n => !n.read).length;
  
  // Initialize edit name when profile opens
  useEffect(() => {
    if (showEditProfile && playerData?.username) {
      setEditName(playerData.username);
    }
  }, [showEditProfile, playerData]);
  
  // Picture Grid enabled state - uses localStorage directly
  // Default to TRUE (enabled) if not set
  const [pictureGridEnabled, setPictureGridEnabled] = useState(() => {
    const saved = localStorage.getItem('pictureGridEnabled');
    // If never set, default to true (enabled)
    if (saved === null) {
      localStorage.setItem('pictureGridEnabled', 'true');
      return true;
    }
    return saved === 'true';
  });
  
  // Toggle picture grid enabled/disabled
  const handleTogglePictureGrid = () => {
    const newValue = !pictureGridEnabled;
    setPictureGridEnabled(newValue);
    localStorage.setItem('pictureGridEnabled', newValue.toString());
    
    // Also update the pictureGridSettings for consistency
    if (onUpdatePictureGridSettings && pictureGridSettings) {
      onUpdatePictureGridSettings({
        ...pictureGridSettings,
        enabled: newValue
      });
    }
  };
  
  // Handle name update
  const handleUpdateName = async () => {
    if (!editName.trim() || editName.trim().length < 2) {
      setEditError('Name must be at least 2 characters');
      return;
    }
    
    setEditLoading(true);
    setEditError('');
    setEditSuccess('');
    
    try {
      const token = localStorage.getItem('trak_token');
      const response = await fetch(`${API_URL}/profile/update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name: editName.trim() })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        setEditError(data.error || 'Failed to update name');
      } else {
        setEditSuccess('✓ Name updated successfully!');
        if (onUpdateProfile) {
          onUpdateProfile({ ...playerData, username: editName.trim(), name: editName.trim() });
        }
        setTimeout(() => setEditSuccess(''), 3000);
      }
    } catch (err) {
      setEditError('Connection failed');
    }
    
    setEditLoading(false);
  };
  
  // Handle password change
  const handleChangePassword = async () => {
    setPasswordError('');
    setPasswordSuccess('');
    
    if (!currentPassword) {
      setPasswordError('Please enter current password');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    
    setPasswordLoading(true);
    
    try {
      const token = localStorage.getItem('trak_token');
      const response = await fetch(`${API_URL}/profile/change-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          current_password: currentPassword,
          new_password: newPassword 
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        setPasswordError(data.error || 'Failed to change password');
      } else {
        setPasswordSuccess('✓ Password changed successfully!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setShowPasswordReset(false);
        setTimeout(() => setPasswordSuccess(''), 3000);
      }
    } catch (err) {
      setPasswordError('Connection failed');
    }
    
    setPasswordLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-900 pb-20">
      <div className="max-w-md mx-auto px-4 py-4">
        
        {/* Header */}
        <h1 className="text-white text-2xl font-bold mb-4 flex items-center gap-2">
          <span>⚙️</span> Settings
        </h1>

        {/* Profile Card */}
        <div className="bg-gray-800 rounded-xl p-4 mb-4 border border-gray-700">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-teal-500 rounded-full flex items-center justify-center text-3xl">
              {playerData?.avatar || '👤'}
            </div>
            <div className="flex-1">
              <p className="text-white font-bold text-lg">{playerData?.username || playerData?.name || 'Player'}</p>
              <p className="text-gray-400 text-sm">ID: #{playerData?.playerId || playerData?.player_id?.slice(0,8) || '0000'}</p>
            </div>
            <button
              onClick={() => setShowEditProfile(!showEditProfile)}
              className="bg-blue-600 hover:bg-blue-500 text-white px-3 py-1 rounded-lg text-sm font-bold"
            >
              ✏️ Edit
            </button>
          </div>
          <div className="text-gray-400 text-sm">
            <p>📧 {playerData?.email || 'No email set'}</p>
            <p>📱 {playerData?.countryCode} {playerData?.phoneNumber || playerData?.phone || 'No phone set'}</p>
          </div>
        </div>
        
        {/* Edit Profile Section */}
        {showEditProfile && (
          <div className="bg-gray-800/80 rounded-xl p-4 mb-4 border border-blue-500">
            <h3 className="text-white font-bold mb-3 flex items-center gap-2">
              <span>✏️</span> Edit Profile
            </h3>
            
            {/* Change Name */}
            <div className="mb-4">
              <label className="text-gray-400 text-xs block mb-1">Display Name</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="flex-1 bg-gray-700 text-white px-3 py-2 rounded-lg text-sm"
                  placeholder="Your name"
                  maxLength={30}
                />
                <button
                  onClick={handleUpdateName}
                  disabled={editLoading}
                  className="bg-green-600 hover:bg-green-500 text-white px-4 py-2 rounded-lg text-sm font-bold"
                >
                  {editLoading ? '...' : 'Save'}
                </button>
              </div>
              {editError && <p className="text-red-400 text-xs mt-1">{editError}</p>}
              {editSuccess && <p className="text-green-400 text-xs mt-1">{editSuccess}</p>}
            </div>
            
            {/* Change Password Toggle */}
            <button
              onClick={() => setShowPasswordReset(!showPasswordReset)}
              className="text-yellow-400 text-sm hover:text-yellow-300 flex items-center gap-1"
            >
              🔐 {showPasswordReset ? 'Hide' : 'Change Password'}
            </button>
            
            {/* Password Reset Form */}
            {showPasswordReset && (
              <div className="mt-3 p-3 bg-gray-700/50 rounded-lg">
                <div className="space-y-2">
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full bg-gray-600 text-white px-3 py-2 rounded-lg text-sm"
                    placeholder="Current password"
                  />
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full bg-gray-600 text-white px-3 py-2 rounded-lg text-sm"
                    placeholder="New password (min 6 chars)"
                  />
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full bg-gray-600 text-white px-3 py-2 rounded-lg text-sm"
                    placeholder="Confirm new password"
                  />
                  <button
                    onClick={handleChangePassword}
                    disabled={passwordLoading}
                    className="w-full bg-yellow-600 hover:bg-yellow-500 text-black font-bold py-2 rounded-lg text-sm"
                  >
                    {passwordLoading ? 'Changing...' : '🔐 Change Password'}
                  </button>
                  {passwordError && <p className="text-red-400 text-xs">{passwordError}</p>}
                  {passwordSuccess && <p className="text-green-400 text-xs">{passwordSuccess}</p>}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Notifications */}
        <button
          onClick={() => setShowNotifications(!showNotifications)}
          className="w-full bg-gray-800 rounded-xl p-4 mb-4 border border-gray-700 flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <span className="text-2xl">🔔</span>
            <span className="text-white font-bold">Notifications</span>
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                {unreadCount}
              </span>
            )}
            <span className="text-gray-400">{showNotifications ? '▼' : '▶'}</span>
          </div>
        </button>

        {showNotifications && (
          <div className="bg-gray-800/50 rounded-xl p-3 mb-4 max-h-60 overflow-y-auto">
            {notifications.length > 0 ? notifications.map(notif => (
              <div 
                key={notif.id}
                onClick={() => onMarkNotificationRead && onMarkNotificationRead(notif.id)}
                className={`p-3 rounded-lg mb-2 cursor-pointer ${
                  notif.read ? 'bg-gray-700/50' : 'bg-blue-700/50 border border-blue-500'
                }`}
              >
                <div className="flex justify-between items-start mb-1">
                  <p className={`font-bold text-sm ${notif.read ? 'text-gray-300' : 'text-white'}`}>
                    {notif.subject}
                  </p>
                  {!notif.read && <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded">New</span>}
                </div>
                <p className="text-gray-400 text-xs">{notif.message}</p>
              </div>
            )) : (
              <p className="text-gray-500 text-center py-4">No notifications</p>
            )}
          </div>
        )}

        {/* Handedness */}
        <div className="bg-gray-800 rounded-xl p-4 mb-4 border border-gray-700">
          <h3 className="text-white font-bold mb-3 flex items-center gap-2">
            <span>✋</span> Handedness
          </h3>
          <p className="text-gray-400 text-sm mb-3">
            Choose which hand you prefer for button placement
          </p>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => onUpdateHandedness && onUpdateHandedness('left')}
              className={`p-3 rounded-xl flex items-center justify-center gap-2 transition-all ${
                playerData?.handedness === 'left'
                  ? 'bg-gradient-to-r from-blue-500 to-teal-500 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              <span className="text-xl">🫲</span>
              <span className="font-bold">Left</span>
            </button>
            <button
              onClick={() => onUpdateHandedness && onUpdateHandedness('right')}
              className={`p-3 rounded-xl flex items-center justify-center gap-2 transition-all ${
                playerData?.handedness === 'right'
                  ? 'bg-gradient-to-r from-blue-500 to-teal-500 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              <span className="text-xl">🫱</span>
              <span className="font-bold">Right</span>
            </button>
          </div>
        </div>

        {/* Picture Grid Toggle */}
        <div className="bg-gray-800 rounded-xl p-4 mb-4 border border-gray-700">
          <h3 className="text-white font-bold mb-3 flex items-center gap-2">
            <span>🖼️</span> Picture Grid
          </h3>
          <p className="text-gray-400 text-sm mb-3">
            Show picture grid bonus game between questions
          </p>
          <button
            onClick={handleTogglePictureGrid}
            className={`w-full p-3 rounded-xl flex items-center justify-between transition-all ${
              pictureGridEnabled
                ? 'bg-gradient-to-r from-green-500 to-emerald-500'
                : 'bg-gray-700'
            }`}
          >
            <span className="text-white font-bold">
              {pictureGridEnabled ? '✓ Picture Grid Enabled' : '✗ Picture Grid Disabled'}
            </span>
            <div className={`w-12 h-6 rounded-full flex items-center p-1 transition-all ${
              pictureGridEnabled ? 'bg-green-700' : 'bg-gray-600'
            }`}>
              <div className={`w-4 h-4 bg-white rounded-full transition-all ${
                pictureGridEnabled ? 'ml-auto' : 'ml-0'
              }`} />
            </div>
          </button>
        </div>

        {/* Sound Settings (Placeholder) */}
        <div className="bg-gray-800 rounded-xl p-4 mb-4 border border-gray-700">
          <h3 className="text-white font-bold mb-3 flex items-center gap-2">
            <span>🔊</span> Sound
          </h3>
          <div className="flex items-center justify-between">
            <span className="text-gray-300">Sound Effects</span>
            <div className="w-12 h-6 bg-green-500 rounded-full flex items-center p-1">
              <div className="w-4 h-4 bg-white rounded-full ml-auto" />
            </div>
          </div>
        </div>

        {/* App Info */}
        <div className="bg-gray-800 rounded-xl p-4 mb-4 border border-gray-700">
          <h3 className="text-white font-bold mb-3 flex items-center gap-2">
            <span>ℹ️</span> About
          </h3>
          <div className="space-y-2 text-sm text-gray-400">
            <p>TRAKtag Quiz v2025.12.13</p>
            <p>The Ultimate Quiz Game</p>
            <div className="flex gap-4 mt-3 flex-wrap">
              <a 
                href="/help" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-yellow-400 hover:text-yellow-300 font-bold"
              >
                📖 How to Play
              </a>
            </div>
            <div className="flex gap-4 mt-2 flex-wrap">
              <a 
                href="/terms" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300"
              >
                Terms
              </a>
              <a 
                href="/privacy" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300"
              >
                Privacy
              </a>
              <a 
                href="/gdpr" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300"
              >
                GDPR
              </a>
            </div>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="bg-gray-800 rounded-xl p-4 border border-red-500/30">
          <h3 className="text-red-400 font-bold mb-3 flex items-center gap-2">
            <span>⚠️</span> Danger Zone
          </h3>
          <button className="text-red-400 text-sm hover:text-red-300">
            Reset All Progress
          </button>
        </div>

      </div>
    </div>
  );
};

export default SettingsTab;
